import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Button, Box, Typography,
    TextField, Alert, Slide, Stepper, Step, StepLabel, Fade,
    IconButton, InputAdornment, Zoom, CircularProgress
} from '@mui/material';
import {
    ArrowBack, CheckCircle, Lock, Person, Badge, VpnKey,
    Visibility, VisibilityOff, Email, Security
} from '@mui/icons-material';
import axios from 'axios';
import { API_CONFIG } from '../config/authConfig';

const API_URL = API_CONFIG.BASE_URL.replace(/\/api$/, '');

// Шаги восстановления
const STEPS = [
    { step: 1, title: 'Логин', icon: <Person /> },
    { step: 2, title: 'Способ', icon: <Security /> },
    { step: 3, title: 'Подтверждение', icon: <Badge /> },
    { step: 4, title: 'Пароль', icon: <VpnKey /> },
    { step: 5, title: 'Готово', icon: <CheckCircle /> }
];

// Стили TextField для тёмной темы
const inputStyles = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        background: '#1a1a1a',
        borderRadius: '12px',
        color: '#fff',
        '&:hover fieldset': { borderColor: '#6366f1' },
        '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 2 },
        '& fieldset': { borderColor: '#374151' }
    },
    '& .MuiInputLabel-root': {
        color: '#9ca3af',
        '&.Mui-focused': { color: '#6366f1' }
    }
};

interface Props {
    open: boolean;
    onClose: () => void;
    onComplete?: (login: string) => void;
}

type RecoveryMethod = 'mail' | 'data';

const PasswordResetDialogWithWA: React.FC<Props> = ({ open, onClose, onComplete }) => {
    // Основные состояния диалога
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Данные пользователя
    const [login, setLogin] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [canUseEmail, setCanUseEmail] = useState(false);
    const [canUseData, setCanUseData] = useState(false);
    const [recoveryMethod, setRecoveryMethod] = useState<RecoveryMethod>('mail');

    // Одноразовый криптографический токен сброса для шага 4
    const [resetToken, setResetToken] = useState<string | null>(null);

    // Код подтверждения на почту
    const [mailCode, setMailCode] = useState(['', '', '', '', '', '']);
    const [codeSent, setCodeSent] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Данные для верификации по сотруднику (ИИН + ФИО)
    const [iin, setIin] = useState('');
    const [fullName, setFullName] = useState('');

    // Новый пароль
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Таймер обратного отсчета для cooldown
    useEffect(() => {
        let interval: any = null;
        if (cooldown > 0) {
            interval = setInterval(() => {
                setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [cooldown]);

    // Сброс всех состояний при закрытии
    const handleClose = () => {
        setStep(1);
        setLogin('');
        setError(null);
        setMaskedEmail('');
        setCanUseEmail(false);
        setCanUseData(false);
        setRecoveryMethod('mail');
        setResetToken(null);
        setMailCode(['', '', '', '', '', '']);
        setCodeSent(false);
        setCooldown(0);
        setIin('');
        setFullName('');
        setNewPassword('');
        setConfirmPassword('');
        onClose();
    };

    // Шаг 1: Проверка логина и доступных методов
    const checkUsername = async () => {
        if (!login.trim()) {
            setError('Введите логин учетной записи');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/v2/auth/password-reset/check-user`, { login: login.trim() });

            if (response.data?.success) {
                const { maskedEmail: mEmail, canUseEmail: hasEmail, canUseData: hasData } = response.data;
                setMaskedEmail(mEmail || '');
                setCanUseEmail(Boolean(hasEmail));
                setCanUseData(Boolean(hasData));

                // Приоритетно выбираем почту, если доступна, иначе данные
                setRecoveryMethod(hasEmail ? 'mail' : 'data');
                setStep(2);
            } else {
                setError(response.data?.message || 'Пользователь не найден');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка проверки пользователя');
        } finally {
            setLoading(false);
        }
    };

    // Отправка кода подтверждения на корпоративную почту
    const sendMailCode = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/v2/auth/password-reset/send-code`, {
                login: login.trim()
            });

            if (response.data?.success) {
                setCodeSent(true);
                setCooldown(response.data?.cooldownSeconds || 60);
            } else {
                setError(response.data?.message || 'Ошибка при отправке кода');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Не удалось отправить код на почту. Попробуйте еще раз или выберите ввод данных.');
        } finally {
            setLoading(false);
        }
    };

    // Переход из Шага 2 в Шаг 3 при выборе корпоративной почты
    const selectMailAndSend = async () => {
        if (!canUseEmail) return;
        setRecoveryMethod('mail');
        setStep(3);
        await sendMailCode();
    };

    // Переход из Шага 2 в Шаг 3 для метода "Данные"
    const selectDataMethod = () => {
        if (!canUseData) return;
        setRecoveryMethod('data');
        setError(null);
        setStep(3);
    };

    // Проверка введенного 6-значного кода из почты
    const verifyMailCode = async () => {
        const code = mailCode.join('').trim();
        if (code.length !== 6) {
            setError('Введите полный 6-значный код подтверждения');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/v2/auth/password-reset/verify-code`, {
                login: login.trim(),
                code
            });

            if (response.data?.success && response.data?.resetToken) {
                setResetToken(response.data.resetToken);
                setStep(4);
            } else {
                setError(response.data?.message || 'Неверный код подтверждения');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка проверки кода');
        } finally {
            setLoading(false);
        }
    };

    // Проверка введенных данных сотрудника (ИИН 12 цифр + ФИО)
    const verifyUserData = async () => {
        const cleanIin = iin.replace(/\D/g, '').trim();
        if (cleanIin.length !== 12) {
            setError('ИИН должен состоять ровно из 12 цифр');
            return;
        }
        if (!fullName.trim()) {
            setError('Укажите ФИО сотрудника');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/v2/auth/password-reset/verify-data`, {
                login: login.trim(),
                iin: cleanIin,
                fullName: fullName.trim()
            });

            if (response.data?.success && response.data?.resetToken) {
                setResetToken(response.data.resetToken);
                setStep(4);
            } else {
                setError(response.data?.message || 'Введенные данные не совпадают с учетной записью');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка проверки данных');
        } finally {
            setLoading(false);
        }
    };

    // Финальный шаг: сохранение нового пароля с передачей resetToken
    const resetPassword = async () => {
        if (!resetToken) {
            setError('Сессия сброса пароля недействительна. Начните заново.');
            setStep(1);
            return;
        }

        if (newPassword.length < 6) {
            setError('Пароль должен содержать не менее 6 символов');
            return;
        }

        if (/[а-яА-ЯёЁ]/.test(newPassword)) {
            setError('Пароль не должен содержать русские буквы. Используйте только латиницу.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/v2/auth/password-reset/confirm-reset`, {
                resetToken,
                newPassword
            });

            if (response.data?.success) {
                setStep(5);
            } else {
                setError(response.data?.message || 'Ошибка обновления пароля');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка сброса пароля');
        } finally {
            setLoading(false);
        }
    };

    // Обработка посимвольного ввода 6 цифр кода
    const handleCodeChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newCode = [...mailCode];
        newCode[index] = value.slice(-1);
        setMailCode(newCode);

        if (value && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }
    };

    const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !mailCode[index] && index > 0) {
            codeInputRefs.current[index - 1]?.focus();
        }
    };

    const handleCodePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newCode = [...mailCode];
        text.split('').forEach((d, i) => { if (i < 6) newCode[i] = d; });
        setMailCode(newCode);
    };

    // Рендер контента диалога по шагам
    const renderContent = () => {
        // Шаг 1: Логин
        if (step === 1) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: '#fff', fontWeight: 600 }}>
                        Введите ваш логин
                    </Typography>
                    <TextField
                        label="Логин или почта"
                        fullWidth
                        value={login}
                        onChange={(e) => setLogin(e.target.value)}
                        disabled={loading}
                        autoFocus
                        placeholder="ap123 или user@zerdegroup.kz"
                        sx={inputStyles}
                        onKeyDown={(e) => e.key === 'Enter' && checkUsername()}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </Box>
            );
        }

        // Шаг 2: Выбор способа
        if (step === 2) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: '#fff', fontWeight: 600 }}>
                        Выберите способ подтверждения
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        {/* 1. Корпоративная почта (@zerdegroup.kz) */}
                        <Box
                            onClick={selectMailAndSend}
                            sx={{
                                flex: 1,
                                p: 2.5,
                                borderRadius: 3,
                                border: `2px solid ${recoveryMethod === 'mail' ? '#6366f1' : '#374151'}`,
                                background: recoveryMethod === 'mail' ? 'rgba(99, 102, 241, 0.12)' : '#111',
                                cursor: canUseEmail ? 'pointer' : 'not-allowed',
                                opacity: canUseEmail ? 1 : 0.45,
                                textAlign: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': canUseEmail ? { borderColor: '#818cf8', transform: 'translateY(-2px)' } : {}
                            }}
                        >
                            <Email sx={{ fontSize: 38, color: '#818cf8', mb: 1 }} />
                            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                                Корпоративная почта
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
                                {maskedEmail || '@zerdegroup.kz'}
                            </Typography>
                        </Box>

                        {/* 2. Данные сотрудника (ИИН + ФИО) */}
                        <Box
                            onClick={selectDataMethod}
                            sx={{
                                flex: 1,
                                p: 2.5,
                                borderRadius: 3,
                                border: `2px solid ${recoveryMethod === 'data' ? '#6366f1' : '#374151'}`,
                                background: recoveryMethod === 'data' ? 'rgba(99, 102, 241, 0.12)' : '#111',
                                cursor: canUseData ? 'pointer' : 'not-allowed',
                                opacity: canUseData ? 1 : 0.45,
                                textAlign: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': canUseData ? { borderColor: '#818cf8', transform: 'translateY(-2px)' } : {}
                            }}
                        >
                            <Badge sx={{ fontSize: 38, color: '#6366f1', mb: 1 }} />
                            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                                ИИН + ФИО
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
                                {canUseData ? 'Сверка данных из базы' : 'ИИН не привязан'}
                            </Typography>
                        </Box>
                    </Box>

                    <Alert
                        severity="info"
                        sx={{
                            bgcolor: 'rgba(99, 102, 241, 0.08)',
                            color: '#c7d2fe',
                            border: '1px solid rgba(99, 102, 241, 0.25)',
                            borderRadius: '10px'
                        }}
                    >
                        {recoveryMethod === 'mail'
                            ? 'Одноразовый 6-значный код будет выслан на вашу корпоративную почту @zerdegroup.kz'
                            : 'Введите ИИН (12 цифр) и ФИО сотрудника, указанные в учётной записи'
                        }
                    </Alert>
                </Box>
            );
        }

        // Шаг 3: Подтверждение (Почта или Данные)
        if (step === 3) {
            // Режим Корпоративной почты
            if (recoveryMethod === 'mail') {
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, color: '#fff', fontWeight: 600 }}>
                            Подтверждение через корпоративную почту
                        </Typography>

                        {!codeSent ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress sx={{ color: '#6366f1' }} />
                                <Typography sx={{ mt: 2, color: '#9ca3af' }}>
                                    Отправка кода на {maskedEmail}...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Alert
                                    severity="success"
                                    sx={{ mb: 3, bgcolor: 'rgba(34, 197, 94, 0.1)', color: '#86efac', borderRadius: '10px' }}
                                >
                                    Код подтверждения отправлен на <b>{maskedEmail}</b>
                                </Alert>

                                <Typography variant="body2" sx={{ color: '#9ca3af', mb: 1.5, textAlign: 'center' }}>
                                    Введите 6-значный код из письма:
                                </Typography>

                                <Box
                                    sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}
                                    onPaste={handleCodePaste}
                                >
                                    {mailCode.map((digit, idx) => (
                                        <TextField
                                            key={idx}
                                            inputRef={(el) => (codeInputRefs.current[idx] = el)}
                                            size="small"
                                            value={digit}
                                            onChange={(e) => handleCodeChange(idx, e.target.value)}
                                            onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                                            inputProps={{
                                                maxLength: 1,
                                                style: { textAlign: 'center', fontSize: 22, fontWeight: 'bold', padding: '10px 0' }
                                            }}
                                            sx={{
                                                width: 46,
                                                '& .MuiOutlinedInput-root': {
                                                    background: '#1a1a1a',
                                                    borderRadius: 2,
                                                    color: '#fff',
                                                    '& fieldset': { borderColor: '#374151' },
                                                    '&.Mui-focused fieldset': { borderColor: '#6366f1' }
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={verifyMailCode}
                                    disabled={loading || mailCode.join('').length !== 6}
                                    sx={{
                                        py: 1.5,
                                        background: '#6366f1',
                                        borderRadius: '10px',
                                        fontWeight: 600,
                                        '&:hover': { background: '#4f46e5' },
                                        '&:disabled': { background: '#374151', color: '#6b7280' }
                                    }}
                                >
                                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Подтвердить код'}
                                </Button>

                                <Button
                                    variant="text"
                                    fullWidth
                                    onClick={sendMailCode}
                                    disabled={loading || cooldown > 0}
                                    sx={{ mt: 1.5, color: cooldown > 0 ? '#6b7280' : '#818cf8' }}
                                >
                                    {cooldown > 0 ? `Отправить повторно (${cooldown} сек.)` : 'Отправить код повторно'}
                                </Button>
                            </>
                        )}

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </Box>
                );
            }

            // Режим сверки данных (ИИН + ФИО)
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, color: '#fff', fontWeight: 600 }}>
                        Проверка учетных данных
                    </Typography>
                    <Alert
                        severity="info"
                        sx={{ mb: 3, bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#a5b4fc', borderRadius: '10px' }}
                    >
                        Укажите данные, привязанные к учётной записи
                    </Alert>
                    <TextField
                        label="ИИН (12 цифр)"
                        fullWidth
                        value={iin}
                        onChange={(e) => setIin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                        disabled={loading}
                        inputProps={{ maxLength: 12 }}
                        placeholder="Например: 950101350123"
                        sx={inputStyles}
                    />
                    <TextField
                        label="ФИО сотрудника"
                        fullWidth
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        disabled={loading}
                        placeholder="Например: Иванов Иван Иванович"
                        sx={inputStyles}
                        onKeyDown={(e) => e.key === 'Enter' && verifyUserData()}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </Box>
            );
        }

        // Шаг 4: Новый пароль
        if (step === 4) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: '#fff', fontWeight: 600 }}>
                        Создайте новый пароль
                    </Typography>
                    <TextField
                        label="Новый пароль"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                        helperText="Минимум 6 символов, только латинские буквы и цифры"
                        sx={inputStyles}
                        FormHelperTextProps={{ sx: { color: '#9ca3af' } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: '#9ca3af' }}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        label="Подтвердите пароль"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        sx={inputStyles}
                        onKeyDown={(e) => e.key === 'Enter' && resetPassword()}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </Box>
            );
        }

        // Шаг 5: Готово
        if (step === 5) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Zoom in style={{ transitionDelay: '150ms' }}>
                        <CheckCircle sx={{ fontSize: 68, color: '#22c55e', mb: 2 }} />
                    </Zoom>
                    <Typography variant="h5" sx={{ color: '#22c55e', fontWeight: 700, mb: 1 }}>
                        Пароль успешно изменён!
                    </Typography>
                    <Typography sx={{ color: '#9ca3af', mb: 3 }}>
                        Все предыдущие активные сессии завершены. Теперь вы можете войти в систему с новым паролем.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => {
                            onComplete?.(login);
                            handleClose();
                        }}
                        sx={{
                            py: 1.5,
                            px: 4,
                            borderRadius: '12px',
                            background: '#f3f4f6',
                            color: '#000',
                            fontWeight: 600,
                            '&:hover': { background: '#fff' }
                        }}
                    >
                        Войти в систему
                    </Button>
                </Box>
            );
        }

        return null;
    };

    // Кнопка "Далее" / "Сохранить"
    const handleNext = () => {
        switch (step) {
            case 1: checkUsername(); break;
            case 2:
                if (recoveryMethod === 'mail') selectMailAndSend();
                else selectDataMethod();
                break;
            case 3:
                if (recoveryMethod === 'data') verifyUserData();
                break;
            case 4: resetPassword(); break;
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return login.trim().length > 0;
            case 2: return recoveryMethod === 'mail' ? canUseEmail : canUseData;
            case 3: return recoveryMethod === 'data' ? (iin.replace(/\D/g, '').length === 12 && fullName.trim().length > 0) : false;
            case 4: return newPassword.length >= 6 && newPassword === confirmPassword;
            default: return false;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Slide}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '20px',
                    background: '#0a0a0a',
                    border: '1px solid #374151',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
                }
            }}
        >
            <DialogTitle sx={{
                textAlign: 'center',
                background: '#111',
                color: 'white',
                py: 2.5,
                borderBottom: '1px solid #262626',
                position: 'relative'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <Lock sx={{ color: '#6366f1' }} />
                    <Typography variant="h6" fontWeight="bold">Сброс пароля</Typography>
                </Box>
                {step > 1 && step < 5 && (
                    <IconButton
                        onClick={() => {
                            setError(null);
                            if (step === 3 && codeSent) setCodeSent(false);
                            setStep(step - 1);
                        }}
                        sx={{
                            position: 'absolute',
                            left: 12,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: '#0a0a0a' }}>
                {/* Stepper */}
                <Box sx={{ p: 2.5, pb: 0 }}>
                    <Stepper activeStep={step - 1} alternativeLabel sx={{
                        '& .MuiStepConnector-line': { borderColor: '#262626' },
                        '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
                            borderColor: '#6366f1'
                        },
                        '& .MuiStepIcon-root': { color: '#262626' },
                        '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
                            color: '#6366f1'
                        },
                        '& .MuiStepLabel-label': { fontSize: '0.72rem', color: '#6b7280' },
                        '& .MuiStepLabel-label.Mui-active': {
                            color: '#818cf8',
                            fontWeight: 600
                        }
                    }}>
                        {STEPS.map((s) => (
                            <Step key={s.step}><StepLabel>{s.title}</StepLabel></Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Content */}
                <Fade in key={step} timeout={300}>
                    <Box>{renderContent()}</Box>
                </Fade>

                {/* Action Buttons */}
                {step < 5 && !(step === 3 && recoveryMethod === 'mail') && (
                    <Box sx={{ p: 3, pt: 0, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            onClick={handleClose}
                            disabled={loading}
                            sx={{ color: '#9ca3af', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' } }}
                        >
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={loading || !canProceed()}
                            sx={{
                                minWidth: 110,
                                py: 1.2,
                                borderRadius: '10px',
                                background: '#f3f4f6',
                                color: '#000',
                                fontWeight: 600,
                                '&:hover': { background: '#fff' },
                                '&:disabled': { background: '#262626', color: '#525252' }
                            }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : (step === 4 ? 'Сохранить' : 'Далее')}
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PasswordResetDialogWithWA;
